import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './Modal.module.postcss';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function Modal({ children, contentClassName, allowOverflow = false, ariaLabel = 'Dialog' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const previouslyFocused = document.activeElement;
    const first = el.querySelector(FOCUSABLE);
    if (first) first.focus();

    return () => {
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      const closeBtn = e.currentTarget.querySelector('[data-dismiss]');
      if (closeBtn) closeBtn.click();
      return;
    }

    if (e.key !== 'Tab') return;
    const el = containerRef.current;
    if (!el) return;
    const focusable = el.querySelectorAll(FOCUSABLE);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={styles.ModalContainer}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      <div className={cn(styles.ModalContent, contentClassName, {
        [styles.ModalContent__allowOverflow]: allowOverflow,
      })}>
        {children}
      </div>
    </div>
  );
}

Modal.propTypes = {
  children: PropTypes.node,
  contentClassName: PropTypes.string,
  allowOverflow: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default Modal;
